// ApiClientsPanel.tsx
// Internal admin panel for managing external OAuth2 API clients.
// Only shown to users whose company === 'rockbridge'.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiData, OAuthClient, ApiLogEntry } from '../../../../contexts/apiService';
import { ProducerDefinitionItem } from '../../../../contexts/tableDefinitions';
import './ApiClientsPanel.css';

// ── Types (local only) ────────────────────────────────────────────────────────
// OAuthClient and ApiLogEntry are imported from apiService.

interface NewSecretInfo {
    client_id: string;
    secret: string;
    label: string;
    scopes: string[];
}

// ── Constants ────────────────────────────────────────────────────────────────

const AVAILABLE_SCOPES = [
    { value: 'schedule:read', label: 'Schedule (read)' },
    { value: 'tickets:read', label: 'Tickets (read)' },
    { value: 'leases:read',  label: 'Leases (read)' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function parseScopes(raw: string[] | string): string[] {
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw as string); } catch { return []; }
}

function fmtDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
}

function parseIpInput(raw: string): string[] {
    return raw.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
}

function formatIpDisplay(ips: string[] | null): React.ReactNode {
    const arr: string[] | null = typeof ips === 'string'
        ? (() => { try { return JSON.parse(ips); } catch { return null; } })()
        : ips;
    if (!arr || arr.length === 0)
        return <span className="acp-ip-open">Open</span>;
    return (
        <span title={arr.join('\n')} className="acp-ip-locked">
            🔒 {arr.length} IP{arr.length > 1 ? 's' : ''}
        </span>
    );
}

function statusBadge(isActive: number) {
    return isActive
        ? <span className="acp-badge acp-badge--active">Active</span>
        : <span className="acp-badge acp-badge--inactive">Inactive</span>;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface SecretModalProps {
    info: NewSecretInfo;
    onClose: () => void;
}

const SecretModal: React.FC<SecretModalProps> = ({ info, onClose }) => {
    const [copied, setCopied] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(info.secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownload = () => {
        const payload = {
            label:         info.label,
            client_id:     info.client_id,
            client_secret: info.secret,
            scopes:        info.scopes,
            generated_at:  new Date().toISOString(),
            note:          'Store this file securely. The client_secret cannot be recovered from Rockbridge — only rotated.',
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `rockbridge-api-credentials-${info.label.replace(/\s+/g, '-').toLowerCase()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="acp-overlay">
            <div className="acp-modal acp-modal--md">
                <h3 className="acp-modal-title--warning">Save this secret now</h3>
                <p className="acp-modal-subtitle">
                    This is the only time the secret for <strong>{info.label}</strong> will be shown.
                    It cannot be recovered — only rotated.
                </p>

                <div className="acp-field">
                    <label className="acp-label">Client ID</label>
                    <div className="acp-code-box">{info.client_id}</div>
                </div>

                <div className="acp-field--lg">
                    <label className="acp-label">Client Secret</label>
                    <div className="acp-code-box acp-code-box--secret">{info.secret}</div>
                </div>

                <div className="acp-btn-row">
                    <button onClick={handleCopy} className="acp-btn-copy">
                        {copied ? '✓ Copied!' : 'Copy Secret'}
                    </button>
                    <button onClick={handleDownload} className="acp-btn-download">
                        ↓ Download JSON
                    </button>
                </div>

                <div className="acp-acknowledge">
                    <label className="acp-checkbox-label">
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={e => setAcknowledged(e.target.checked)}
                        />
                        I have saved the secret and understand it will not be shown again.
                    </label>
                </div>

                <div className="acp-modal-footer">
                    <button
                        onClick={onClose}
                        disabled={!acknowledged}
                        className="acp-btn-primary"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

interface LogModalProps {
    clientId: string;
    label: string;
    onClose: () => void;
}

const LogModal: React.FC<LogModalProps> = ({ clientId, label, onClose }) => {
    const { fetchOAuthClientLog } = useApiData();
    const [rows, setRows] = useState<ApiLogEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const perPage = 25;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchPage = useCallback(async (p: number) => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchOAuthClientLog(clientId, p, perPage);
            setRows(data.data);
            setTotal(data.total);
            setPage(p);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [clientId, fetchOAuthClientLog]);

    useEffect(() => { fetchPage(1); }, [fetchPage]);

    const totalPages = Math.max(1, Math.ceil(total / perPage));

    return (
        <div className="acp-overlay">
            <div className="acp-modal acp-modal--lg">
                <div className="acp-modal-header">
                    <h3 className="acp-modal-title">Request log — {label}</h3>
                    <button onClick={onClose} className="acp-btn-close">✕</button>
                </div>

                {error && <div className="acp-error-box">{error}</div>}

                {loading ? (
                    <div className="acp-empty-state">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="acp-empty-state">No log entries yet.</div>
                ) : (
                    <div className="acp-table-wrapper">
                        <table className="acp-table">
                            <thead>
                                <tr className="acp-thead-row">
                                    <th className="acp-th">Time</th>
                                    <th className="acp-th">Method</th>
                                    <th className="acp-th">Endpoint</th>
                                    <th className="acp-th">Status</th>
                                    <th className="acp-th">Source IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.id} className="acp-tr">
                                        <td className="acp-td">{fmtDate(r.requested_at)}</td>
                                        <td className="acp-td">{r.method}</td>
                                        <td className="acp-td acp-td--mono">{r.endpoint}</td>
                                        <td className="acp-td">
                                            <span className={r.response_code < 400 ? 'acp-status-ok' : 'acp-status-err'}>
                                                {r.response_code}
                                            </span>
                                        </td>
                                        <td className="acp-td acp-td--mono">{r.source_ip}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="acp-pagination">
                        <button
                            disabled={page <= 1}
                            onClick={() => fetchPage(page - 1)}
                            className="acp-btn-page"
                        >
                            ← Prev
                        </button>
                        <span className="acp-pagination-info">
                            Page {page} of {totalPages} ({total} total)
                        </span>
                        <button
                            disabled={page >= totalPages}
                            onClick={() => fetchPage(page + 1)}
                            className="acp-btn-page"
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ── ScopesModal ───────────────────────────────────────────────────────────────

interface ScopesModalProps {
    clientId: string;
    label: string;
    currentScopes: string[];
    onClose: () => void;
    onSave: (clientId: string, scopes: string[], oldScopes: string[]) => Promise<void>;
}

const ScopesModal: React.FC<ScopesModalProps> = ({ clientId, label, currentScopes, onClose, onSave }) => {
    const [selected, setSelected] = useState<string[]>(currentScopes);
    const [saving, setSaving] = useState(false);
    const [scopesError, setScopesError] = useState<string | null>(null);

    const toggle = (scope: string) =>
        setSelected(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);

    const handleSave = async () => {
        if (selected.length === 0) { setScopesError('Select at least one scope.'); return; }
        setSaving(true);
        setScopesError(null);
        try {
            await onSave(clientId, selected, currentScopes);
            onClose();
        } catch (e: any) {
            setScopesError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-overlay">
            <div className="acp-modal acp-modal--sm">
                <div className="acp-modal-header acp-modal-header--sm">
                    <h3 className="acp-modal-title">Edit Scopes — {label}</h3>
                    <button onClick={onClose} className="acp-btn-close">✕</button>
                </div>
                <p className="acp-modal-subtitle--sm">
                    Select the permissions this client is allowed to use.
                </p>
                {scopesError && <div className="acp-error-box acp-error-box--spaced">{scopesError}</div>}
                <div className="acp-scopes-list">
                    {AVAILABLE_SCOPES.map(s => (
                        <label key={s.value} className="acp-scope-option">
                            <input
                                type="checkbox"
                                checked={selected.includes(s.value)}
                                onChange={() => toggle(s.value)}
                            />
                            {s.label}
                            <span className="acp-scope-code">({s.value})</span>
                        </label>
                    ))}
                </div>
                <div className="acp-form-actions--spaced">
                    <button onClick={onClose} className="acp-btn-secondary">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="acp-btn-primary">
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── IpsModal ──────────────────────────────────────────────────────────────────

interface IpsModalProps {
    clientId: string;
    label: string;
    currentIps: string[];
    onClose: () => void;
    onSave: (clientId: string, ips: string[], oldIps: string[]) => Promise<void>;
}

const IpsModal: React.FC<IpsModalProps> = ({ clientId, label, currentIps, onClose, onSave }) => {
    const [raw, setRaw] = useState(currentIps.join('\n'));
    const [saving, setSaving] = useState(false);
    const [ipsError, setIpsError] = useState<string | null>(null);

    const handleSave = async () => {
        setSaving(true);
        setIpsError(null);
        try {
            await onSave(clientId, parseIpInput(raw), currentIps);
        } catch (e: any) {
            setIpsError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-overlay">
            <div className="acp-modal acp-modal--sm">
                <div className="acp-modal-header acp-modal-header--sm">
                    <h3 className="acp-modal-title">IP Whitelist — {label}</h3>
                    <button onClick={onClose} className="acp-btn-close">✕</button>
                </div>
                <p className="acp-modal-subtitle--sm">
                    One IP address or CIDR block per line. Leave blank to allow any IP.
                </p>
                {ipsError && <div className="acp-error-box acp-error-box--spaced">{ipsError}</div>}
                <textarea
                    value={raw}
                    onChange={e => setRaw(e.target.value)}
                    placeholder={'192.168.1.100\n10.0.0.0/24'}
                    className="acp-input acp-input--mono acp-textarea-md"
                />
                <div className="acp-form-actions--spaced">
                    <button onClick={onClose} className="acp-btn-secondary">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="acp-btn-primary">
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main panel ─────────────────────────────────────────────────────────────────

const ApiClientsPanel: React.FC = () => {
    const { apiData, handleGetTable, fetchOAuthClients, createOAuthClient, toggleOAuthClient, rotateOAuthClient, updateOAuthClientIps, updateOAuthClientScopes } = useApiData();

    const [clients, setClients] = useState<OAuthClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // New-client form
    const [showNewModal, setShowNewModal] = useState(false);
    const [newLabel, setNewLabel] = useState('');
    const [newProducerId, setNewProducerId] = useState<number | ''>('');
    const [newScopes, setNewScopes] = useState<string[]>([]);
    const [creating, setCreating] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    // Secret reveal modal
    const [pendingSecret, setPendingSecret] = useState<NewSecretInfo | null>(null);

    // Log modal
    const [logTarget, setLogTarget] = useState<{ id: string; label: string } | null>(null);

    // IPs modal
    const [newAllowedIps, setNewAllowedIps] = useState('');
    const [ipsTarget, setIpsTarget] = useState<{ id: string; label: string; ips: string[] } | null>(null);

    // Scopes modal
    const [scopesTarget, setScopesTarget] = useState<{ id: string; label: string; scopes: string[] } | null>(null);

    const producers: ProducerDefinitionItem[] = apiData?.producer_definition ?? [];

    useEffect(() => {
        if (!apiData?.producer_definition) {
            handleGetTable('producer_definition');
        }
        fetchClients();
    }, []);

    // Sync clients from socket-pushed apiData.oauth_clients (other admin sessions)
    useEffect(() => {
        if (apiData?.oauth_clients && apiData.oauth_clients.length > 0) {
            setClients(apiData.oauth_clients);
            setLoading(false);
        }
    }, [apiData?.oauth_clients]);

    const fetchClients = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchOAuthClients();
            setClients(data);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        setFormError(null);
        if (!newLabel.trim()) { setFormError('Label is required.'); return; }
        if (!newProducerId) { setFormError('Producer is required.'); return; }
        if (newScopes.length === 0) { setFormError('Select at least one scope.'); return; }

        setCreating(true);
        try {
            const ips = parseIpInput(newAllowedIps);
            const result = await createOAuthClient(
                newLabel.trim(),
                newProducerId as number,
                newScopes,
                ips.length > 0 ? ips : null,
            );
            setPendingSecret({ client_id: result.client_id, secret: result.client_secret, label: result.label, scopes: newScopes });
            setShowNewModal(false);
            setNewLabel('');
            setNewProducerId('');
            setNewScopes([]);
            setNewAllowedIps('');
        } catch (e: any) {
            setFormError(e.message);
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (clientId: string, currentlyActive: boolean) => {
        try {
            await toggleOAuthClient(clientId, !currentlyActive);
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleRotate = async (clientId: string, label: string, scopes: string[]) => {
        if (!window.confirm(
            `Rotate the secret for "${label}"?\n\nThe existing secret will stop working immediately.`
        )) return;
        try {
            const result = await rotateOAuthClient(clientId);
            setPendingSecret({ client_id: result.client_id, secret: result.client_secret, label, scopes });
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleUpdateIps = async (clientId: string, ips: string[], oldIps: string[]) => {
        await updateOAuthClientIps(clientId, ips.length > 0 ? ips : null, oldIps);
        setIpsTarget(null);
    };

    const handleUpdateScopes = async (clientId: string, scopes: string[], oldScopes: string[]) => {
        await updateOAuthClientScopes(clientId, scopes, oldScopes);
    };

    const toggleScope = (scope: string) => {
        setNewScopes(prev =>
            prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
        );
    };

    return (
        <div className="acp-panel">

            {/* Header row */}
            <div className="acp-header-row">
                <h4 className="acp-panel-title">External API Clients</h4>
                <button onClick={() => { setShowNewModal(true); setFormError(null); }} className="acp-btn-primary">
                    + New Client
                </button>
            </div>

            {/* Error state */}
            {error && <div className="acp-error-box">{error}</div>}

            {/* Loading state */}
            {loading && <div className="acp-empty-state">Loading…</div>}

            {/* Clients table */}
            {!loading && clients.length === 0 && !error && (
                <div className="acp-empty-state">
                    No API clients yet. Create one to get started.
                </div>
            )}

            {!loading && clients.length > 0 && (
                <div className="acp-table-wrapper">
                    <table className="acp-table">
                        <thead>
                            <tr className="acp-thead-row">
                                <th className="acp-th">Label</th>
                                <th className="acp-th">Producer</th>
                                <th className="acp-th">Scopes</th>
                                <th className="acp-th">Status</th>
                                <th className="acp-th">IP Lock</th>
                                <th className="acp-th">Created</th>
                                <th className="acp-th">Last Used</th>
                                <th className="acp-th">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map(c => {
                                const scopes = parseScopes(c.scopes);
                                const active = c.is_active === 1;
                                return (
                                    <tr key={c.client_id} className="acp-tr">
                                        <td className="acp-td">
                                            <div className="acp-client-label">{c.label}</div>
                                            <div className="acp-client-id">
                                                {c.client_id}
                                            </div>
                                        </td>
                                        <td className="acp-td">{c.producer_name}</td>
                                        <td className="acp-td">
                                            {scopes.length > 0
                                                ? scopes.map(s => (
                                                    <span key={s} className="acp-scope-chip">{s}</span>
                                                ))
                                                : <span className="acp-no-scopes">none</span>
                                            }
                                        </td>
                                        <td className="acp-td">{statusBadge(c.is_active)}</td>
                                        <td className="acp-td">{formatIpDisplay(c.allowed_ips)}</td>
                                        <td className="acp-td acp-td--nowrap">{fmtDate(c.created_at)}</td>
                                        <td className="acp-td acp-td--nowrap">{fmtDate(c.last_used_at)}</td>
                                        <td className="acp-td acp-td--nowrap">
                                            <div className="acp-actions">
                                                <button
                                                    onClick={() => handleToggle(c.client_id, active)}
                                                    className={active ? 'acp-btn-danger' : 'acp-btn-secondary'}
                                                    title={active ? 'Deactivate' : 'Activate'}
                                                >
                                                    {active ? 'Deactivate' : 'Activate'}
                                                </button>
                                                <button
                                                    onClick={() => handleRotate(c.client_id, c.label, parseScopes(c.scopes))}
                                                    className="acp-btn-secondary"
                                                    title="Rotate secret"
                                                >
                                                    Rotate
                                                </button>
                                                <button
                                                    onClick={() => setScopesTarget({ id: c.client_id, label: c.label, scopes: parseScopes(c.scopes) })}
                                                    className="acp-btn-ghost"
                                                    title="Edit scopes"
                                                >
                                                    Scopes
                                                </button>
                                                <button
                                                    onClick={() => setIpsTarget({ id: c.client_id, label: c.label, ips: c.allowed_ips ?? [] })}
                                                    className="acp-btn-ghost"
                                                    title="Edit IP whitelist"
                                                >
                                                    IPs
                                                </button>
                                                <button
                                                    onClick={() => setLogTarget({ id: c.client_id, label: c.label })}
                                                    className="acp-btn-ghost"
                                                    title="View request log"
                                                >
                                                    Log
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── New Client Modal ─────────────────────────────────────────── */}
            {showNewModal && (
                <div className="acp-overlay">
                    <div className="acp-modal acp-modal--sm">
                        <div className="acp-modal-header">
                            <h3 className="acp-modal-title">New API Client</h3>
                            <button onClick={() => setShowNewModal(false)} className="acp-btn-close">✕</button>
                        </div>

                        {formError && <div className="acp-error-box acp-error-box--spaced">{formError}</div>}

                        <div className="acp-field">
                            <label className="acp-label">Label *</label>
                            <input
                                type="text"
                                value={newLabel}
                                onChange={e => setNewLabel(e.target.value)}
                                placeholder="e.g. Acme ERP integration"
                                className="acp-input"
                            />
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Producer *</label>
                            <select
                                value={newProducerId}
                                onChange={e => setNewProducerId(e.target.value ? Number(e.target.value) : '')}
                                className="acp-input"
                            >
                                <option value="">— select producer —</option>
                                {producers
                                    .slice()
                                    .sort((a, b) => a.producer_name.localeCompare(b.producer_name))
                                    .map(p => (
                                        <option key={p.id} value={p.id}>{p.producer_name}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className="acp-field--lg">
                            <label className="acp-label">Scopes *</label>
                            <div className="acp-scopes-list">
                                {AVAILABLE_SCOPES.map(s => (
                                    <label key={s.value} className="acp-scope-option">
                                        <input
                                            type="checkbox"
                                            checked={newScopes.includes(s.value)}
                                            onChange={() => toggleScope(s.value)}
                                        />
                                        {s.label}
                                        <span className="acp-scope-code">({s.value})</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="acp-field--lg">
                            <label className="acp-label">
                                IP Whitelist{' '}
                                <span className="acp-label-hint">(optional — one IP or CIDR per line)</span>
                            </label>
                            <textarea
                                value={newAllowedIps}
                                onChange={e => setNewAllowedIps(e.target.value)}
                                placeholder={'Leave blank to allow any IP\n192.168.1.100\n10.0.0.0/24'}
                                className="acp-input acp-input--mono acp-textarea-sm"
                            />
                        </div>

                        <div className="acp-form-actions">
                            <button onClick={() => setShowNewModal(false)} className="acp-btn-secondary">Cancel</button>
                            <button onClick={handleCreate} disabled={creating} className="acp-btn-primary">
                                {creating ? 'Creating…' : 'Create Client'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Secret reveal modal ──────────────────────────────────────── */}
            {pendingSecret && (
                <SecretModal info={pendingSecret} onClose={() => setPendingSecret(null)} />
            )}

            {/* ── Log modal ────────────────────────────────────────────────── */}
            {logTarget && (
                <LogModal
                    clientId={logTarget.id}
                    label={logTarget.label}
                    onClose={() => setLogTarget(null)}
                />
            )}
            {/* ── Scopes modal ──────────────────────────────────────────────────────── */}
            {scopesTarget && (
                <ScopesModal
                    clientId={scopesTarget.id}
                    label={scopesTarget.label}
                    currentScopes={scopesTarget.scopes}
                    onClose={() => setScopesTarget(null)}
                    onSave={handleUpdateScopes}
                />
            )}
            {/* ── IPs modal ─────────────────────────────────────────────────────────── */}
            {ipsTarget && (
                <IpsModal
                    clientId={ipsTarget.id}
                    label={ipsTarget.label}
                    currentIps={ipsTarget.ips}
                    onClose={() => setIpsTarget(null)}
                    onSave={handleUpdateIps}
                />
            )}
        </div>
    );
};

export default ApiClientsPanel;
